import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";

import { EmergencyBanner } from "@/components/EmergencyBanner";
import { Landing } from "@/components/Landing";
import { DemoIntroScreen } from "@/components/DemoIntroScreen";
import { SafetyBriefing } from "@/components/SafetyBriefing";
import { ConsciousnessTriage } from "@/components/ConsciousnessTriage";
import { CprGuidance } from "@/components/CprGuidance";
import { BrandFooter } from "@/components/BrandFooter";
import { PoweredByFooter } from "@/components/PoweredByFooter";
import {
    loadLocation,
    formatLocationLabel,
    clearLocation as clearStoredLocation,
} from "@/lib/locationStore";
import { useWakeLock } from "@/lib/useWakeLock";
import { useI18n } from "@/lib/i18n";
import { setLang as setSpeechLang } from "@/lib/speech";
import { useTracking, trackEvent, updateSessionFlag } from "@/lib/useTracking";
import { useQr } from "@/lib/qrContext";
import { EmailCapturePopup } from "@/components/EmailCapturePopup";
import { QrCreateModal } from "@/components/QrCreateModal";
import { StickerToast } from "@/components/StickerToast";

const EMAIL_POPUP_DELAY_MS = 10_000;
// localStorage cooldown – ~14 days. Cleared automatically after that so
// returning visitors get a second chance, but no nag on every reload.
const EMAIL_POPUP_SHOWN_KEY = "sa_email_popup_shown_at";
const EMAIL_POPUP_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

// steps: "landing" | "demo_intro" | "safety" | "triage" | "cpr"
function App() {
    const { lang, t } = useI18n();
    const { qrId, exists, logScan, brandingWarning } = useQr();
    const [step, setStep] = useState("landing");
    const [mode, setMode] = useState(null); // "real" | "demo"
    const [decisions, setDecisions] = useState([]); // breadcrumb of answers
    const [savedLocation, setSavedLocation] = useState(null);

    useWakeLock(step !== "landing");

    // Page-view analytics. Tracks every step transition + language change.
    useTracking({ page: step, locale: lang, demoMode: mode === "demo" });

    // Keep speech.js in sync with the active language for both ElevenLabs
    // cache keying and Web Speech locale selection.
    useEffect(() => {
        setSpeechLang(lang);
    }, [lang]);

    // Load any persisted location on first mount.
    useEffect(() => {
        setSavedLocation(loadLocation());
    }, []);

    // ----- Global email-capture popup -----
    // Only shown when the visitor did NOT arrive via a personalised QR code.
    // Otherwise the URL owner has already opted in and we don't want to
    // pester them with marketing.
    const [showEmailPopup, setShowEmailPopup] = useState(false);
    useEffect(() => {
        if (qrId) return;
        let recentlyShown = false;
        try {
            const ts = parseInt(
                localStorage.getItem(EMAIL_POPUP_SHOWN_KEY) || "0",
                10,
            );
            recentlyShown = ts && Date.now() - ts < EMAIL_POPUP_COOLDOWN_MS;
        } catch {
            /* storage blocked – fall through */
        }
        if (recentlyShown) return;
        const id = setTimeout(() => setShowEmailPopup(true), EMAIL_POPUP_DELAY_MS);
        return () => clearTimeout(id);
    }, [qrId]);

    // Auto-start demo mode when called via /?demo=1 (used by the marketing
    // landing page's "Demo starten" button).
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            if (params.get("demo") === "1") {
                setMode("demo");
                setDecisions([]);
                setStep("demo_intro");
                trackEvent("demo_started", {
                    locale: lang,
                    demoMode: true,
                    page: "landing",
                });
                // clean the URL so a refresh doesn't relaunch the demo
                const url = new URL(window.location.href);
                url.searchParams.delete("demo");
                window.history.replaceState({}, "", url.toString());
            }
        } catch {
            /* ignore */
        }
    }, []);

    const closeEmailPopup = () => {
        setShowEmailPopup(false);
        try {
            localStorage.setItem(EMAIL_POPUP_SHOWN_KEY, Date.now().toString());
        } catch {
            /* ignore */
        }
    };

    const resetToLanding = () => {
        setStep("landing");
        setMode(null);
        setDecisions([]);
    };

    const handleClearLocation = () => {
        clearStoredLocation();
        setSavedLocation(null);
    };

    const locationLabel = savedLocation ? formatLocationLabel(savedLocation) : "";
    const demoMode = mode === "demo" && step !== "landing";

    // Measure the real header height so the main content can never slip
    // behind it. The header is fixed and its size varies depending on
    // viewport (SafetyAssist logo + scan-call text + optional custom
    // brand bar + optional location bar + demo gradient).
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(120);
    useLayoutEffect(() => {
        const el = document.querySelector('[data-testid="emergency-banner"]');
        if (!el) return undefined;
        headerRef.current = el;
        const measure = () => setHeaderHeight(el.getBoundingClientRect().height);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        window.addEventListener("resize", measure);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [demoMode, locationLabel, qrId]);

    return (
        <div
            className="min-h-screen bg-zinc-50"
            style={
                demoMode
                    ? {
                          backgroundColor: "#fdba74",
                      }
                    : undefined
            }
            data-testid={demoMode ? "demo-page-background" : undefined}
        >
            <EmergencyBanner
                locationLabel={locationLabel}
                onClearLocation={handleClearLocation}
                demoMode={demoMode}
                showResolverWarning={
                    brandingWarning && step === "landing" && !demoMode
                }
            />

            <main
                style={{ paddingTop: headerHeight + 16 }}
                className="pb-10"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-6 sm:mt-8 space-y-6 sm:space-y-8">
                    {step === "landing" && (
                        <Landing
                            onStart={(m) => {
                                setMode(m);
                                setDecisions([]);
                                // Demo visitors land on an explanatory
                                // intermediate page (DGUV poster + "Weiter"
                                // button) before the safety briefing kicks
                                // in. Real emergencies still jump straight
                                // to safety.
                                setStep(m === "demo" ? "demo_intro" : "safety");
                                trackEvent(
                                    m === "demo" ? "demo_started" : "emergency_pressed",
                                    { locale: lang, demoMode: m === "demo", page: "landing" },
                                );
                                // Background log to Makrolog when this URL has
                                // a valid existing QR id.
                                logScan({
                                    demoMode: m === "demo",
                                    page: m === "demo" ? "demo_started" : "emergency_pressed",
                                });
                            }}
                        />
                    )}

                    {step === "demo_intro" && (
                        <DemoIntroScreen
                            onContinue={() => setStep("safety")}
                        />
                    )}

                    {step === "safety" && (
                        <SafetyBriefing
                            mode={mode}
                            onNext={() => setStep("triage")}
                            onSkipToCpr={
                                mode === "demo"
                                    ? () => {
                                          setDecisions([
                                              "consciousness:no",
                                              "breathing:no",
                                              "called112",
                                          ]);
                                          trackEvent("cpr_started", {
                                              locale: lang,
                                              demoMode: true,
                                              page: "cpr",
                                          });
                                          updateSessionFlag({ key: "reachedCpr", value: true });
                                          setStep("cpr");
                                      }
                                    : undefined
                            }
                        />
                    )}

                    {step === "triage" && (
                        <ConsciousnessTriage
                            mode={mode}
                            decisions={decisions}
                            setDecisions={setDecisions}
                            onStartCpr={() => {
                                trackEvent("cpr_started", {
                                    locale: lang,
                                    demoMode: mode === "demo",
                                    page: "cpr",
                                });
                                updateSessionFlag({ key: "reachedCpr", value: true });
                                setStep("cpr");
                            }}
                        />
                    )}

                    {step === "cpr" && (
                        <CprGuidance
                            mode={mode}
                            decisions={decisions}
                        />
                    )}

                    {step !== "landing" && (
                        <div className="pt-2 text-center text-xs text-zinc-400">
                            <button
                                data-testid="reset-link"
                                onClick={resetToLanding}
                                className="underline hover:text-zinc-900 font-display font-semibold"
                            >
                                {t("reset_link")}
                            </button>
                        </div>
                    )}

                    <BrandFooter />
                    <PoweredByFooter />
                </div>
            </main>

            <Toaster position="top-center" richColors />
            <StickerToast />

            {qrId && exists === false && <QrCreateModal />}

            {showEmailPopup && (
                <EmailCapturePopup
                    onClose={closeEmailPopup}
                    onSubmitted={closeEmailPopup}
                />
            )}
        </div>
    );
}

export default App;
